import { CustomAuthMethodTypeEnum } from '@pypestream/api-services';
import {
  Button,
  PageBody,
  PageSection,
  Snackbar,
  Spacer,
  Spinner,
  Stack,
  TextTitle,
} from '@pypestream/design-system';
import { TranslationComponent } from '@pypestream/translations';
import { FC, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { OrganizationRoleNames, useAuthRole } from '../../../hooks';
import {
  sendManagerEvent,
  useManagerCtxSelector,
  useManagerStateMatches,
  useManagerStateMatchesOneOf,
} from '../../../xstate/app.xstate';
import { IPage } from '../../types';
import {
  CustomAuthMethodSettingsType,
  DefaultAuthMethodSettingsType,
  ReturnFormType,
} from './create-organization-fields';
import { OrganizationDetails } from './organization-details';

export type FormOrgInfoType = {
  name: string;
  picture?: string;
  accountIconId?: string;
  accountManagerId?: string;
  customAuthMethods?: CustomAuthMethodSettingsType[];
  defaultAuthMethods?: DefaultAuthMethodSettingsType[];
};

export const OIDC_AUTH_METHOD_DEFAULT_VALUES = {
  name: 'Custom SSO via OpenID Connect',
  type: CustomAuthMethodTypeEnum.Oidc,
  isActive: false,
  isDefault: false,
  accountAuthMethodSettingId: '',
  clientId: '',
  clientSecret: '',
  issuerURL: '',
};

export const SAML_AUTH_METHOD_DEFAULT_VALUES = {
  name: 'Custom SSO via SAML2',
  type: CustomAuthMethodTypeEnum.Saml,
  isActive: false,
  isDefault: false,
  accountAuthMethodSettingId: '',
  settings: {
    nameIDPolicyFormat: '',
  },
  clientId: '',
  clientSecret: '',
  issuerURL: '',
  usernameAttribute: '',
  emailAttribute: '',
  entityIssuer: '',
};

export const CreateOrganization: FC<IPage> = () => {
  const navigate = useNavigate();

  const { routes, availableDefaultAuthMethods, orgDetails } =
    useManagerCtxSelector((ctx) => ({
      routes: ctx.routes,
      availableDefaultAuthMethods: ctx.defaultAuthMethods,
      orgDetails: ctx.orgDetails,
    }));

  const isOrgDetailsLoaded =
    useManagerStateMatches('orgRelated.ready.orgDetails.idle') && !!orgDetails;

  const isOrgAdding = useManagerStateMatches(
    'orgRelated.ready.orgDetails.adding'
  );

  const hasInflightNetworkRequest = useManagerStateMatchesOneOf([
    'orgRelated.ready.orgDetails.loading',
    'orgRelated.ready.orgDetails.updating',
    'orgRelated.ready.orgDetails.deleting',
    'orgRelated.ready.orgDetails.adding',
  ]);

  const isAdminRole = useAuthRole([
    OrganizationRoleNames.ADMIN,
    OrganizationRoleNames.SUPER_ADMIN,
  ]);

  const navigateToChildOrgs = useCallback(
    () => navigate(routes.orgs),
    [navigate, routes.orgs]
  );

  const form = useForm<FormOrgInfoType>({
    defaultValues: {
      name: '',
      picture: '',
      accountIconId: '',
      accountManagerId: '',
      customAuthMethods: [
        OIDC_AUTH_METHOD_DEFAULT_VALUES,
        SAML_AUTH_METHOD_DEFAULT_VALUES,
      ],
      defaultAuthMethods: [
        ...(availableDefaultAuthMethods || []).map((method) => ({
          id: method.id || '',
          name: method.name || '',
          isActive: false,
          isDefault: false,
          isCustomAuthMethod: false,
        })),
      ],
    },
  });

  useEffect(() => {
    if (isOrgDetailsLoaded) {
      form.resetField('defaultAuthMethods', {
        defaultValue: [
          ...(availableDefaultAuthMethods || []).map((method) => ({
            id: method.id || '',
            name: method.name || '',
            isActive: false,
            isDefault: false,
            isCustomAuthMethod: false,
          })),
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrgDetailsLoaded]);

  const onSubmit = (values: FormOrgInfoType) => {
    sendManagerEvent({
      type: 'manager.addOrg',
      accountName: values.name,
      accountIconId: values.accountIconId,
      callback: (res) => {
        if (res) {
          sendManagerEvent('manager.childOrgs.loadChildOrgs');
          navigateToChildOrgs();
        }
      },

      defaultAuthMethods: (
        (values.defaultAuthMethods || []).filter(
          (defaultAuthMethod) => defaultAuthMethod.isActive
        ) || []
      ).map((defaultAuthMethod) => defaultAuthMethod.id!),
      customAuthMethods: values.customAuthMethods
        ?.filter((customAuthMethod) => customAuthMethod.isActive)
        .map(
          ({
            isActive,
            isDefault,
            accountAuthMethodSettingId,
            ...restFields
          }) => restFields
        ),
    });
  };

  return (
    <PageBody background="none">
      <TextTitle
        test-id="create-org-title"
        i18nKey="manager/organizations:createOrg.title"
      >
        <TranslationComponent i18nKey="manager/organizations:createOrg.title">
          Create Child Org
        </TranslationComponent>
      </TextTitle>
      {(hasInflightNetworkRequest && !isOrgDetailsLoaded && (
        <PageSection>
          <Stack justifyContent="center" alignItems="center">
            <Spinner />
          </Stack>
        </PageSection>
      )) || (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Spacer size="large" />
          <OrganizationDetails
            form={form as unknown as ReturnFormType}
            isAdminRole={isAdminRole}
            isEdit={false}
            hasInflightNetworkRequest={!!hasInflightNetworkRequest}
          />
          <Snackbar
            id="cerate-org-snackbar"
            open={
              !!Object.keys(form.formState.dirtyFields).length && isAdminRole
            }
          >
            When you are ready, create the Org
            <Button
              test-id="cancel-creation-org-btn"
              variant="secondary"
              size="large"
              onClick={() => {
                form.reset();
              }}
              disabled={!!hasInflightNetworkRequest || isOrgAdding}
              slot="action"
            >
              <TranslationComponent i18nKey="manager/organizations:createOrg.cancel">
                Cancel
              </TranslationComponent>
            </Button>
            <Button
              test-id="create-org-btn"
              size="large"
              onClick={form.handleSubmit(onSubmit)}
              disabled={
                !Object.keys(form.formState.dirtyFields).length ||
                !!hasInflightNetworkRequest ||
                isOrgAdding
              }
              slot="action"
            >
              {hasInflightNetworkRequest || isOrgAdding ? (
                <TranslationComponent i18nKey="manager/organizations:createOrg.creating">
                  Creating Org...
                </TranslationComponent>
              ) : (
                <TranslationComponent i18nKey="manager/organizations:createOrg.create">
                  Create Org
                </TranslationComponent>
              )}
            </Button>
          </Snackbar>
        </form>
      )}
    </PageBody>
  );
};
